import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { COIN_PRICE_CENTS, MIN_COINS, formatEuro, grossFromNet } from "@/lib/salonturbo";
import { homeFaqs, faqJsonLd } from "@/lib/faq-content";
import {
  Scissors,
  CalendarX2,
  MousePointerClick,
  Users,
  ShieldCheck,
  SlidersHorizontal,
  GraduationCap,
  Check,
  ArrowRight,
  Zap,
  ChevronDown,
  Phone,

  Info,
  X,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { GuideCard } from "@/components/GuideCard";
import { BoostStorySequence } from "@/components/home/BoostStorySequence";

import salonInterior from "@/assets/salon-interior.jpg";
import hairBob from "@/assets/hair-bob.jpg";
import stylistHands from "@/assets/stylist-hands.jpg";
import hairFade from "@/assets/hair-fade.jpg";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "SalonTurbo – Google-Werbung für Friseursalons, tageweise" },
      {
        name: "description",
        content:
          "SalonTurbo schaltet für Friseursalons Google-Suchanzeigen an Tagen mit freien Stühlen. Ein Coin = ein Boost-Tag. Ohne Agentur, ohne eigenes Google-Konto, ohne Mindestlaufzeit.",
      },
      { property: "og:title", content: "SalonTurbo – Google-Werbung für Friseursalons, tageweise" },
      {
        property: "og:description",
        content:
          "Wähle freie Tage im Kalender. Wir schalten automatisch lokale Google-Anzeigen und eine eigene Salon-Seite mit Anruf-Button.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(faqJsonLd(homeFaqs)),
      },
    ],
  }),
  component: Home,
});


function PhoneMockup() {
  const weekdays = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
  // Beispielmonat: 1. fällt auf Montag; Mi 10. und Do 11. sind ausgewählt
  const days = Array.from({ length: 21 }, (_, i) => i + 1);
  const selected = [10, 11];
  const closed = [7, 14, 21];

  return (
    <div className="mx-auto w-[248px] rounded-[2.4rem] border-8 border-foreground/90 bg-foreground/90 shadow-2xl sm:w-[276px]">
      <div className="overflow-hidden rounded-[1.8rem] bg-background">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <span className="flex items-center gap-1.5">
            <Scissors className="size-3.5 text-primary" />
            <span className="wordmark text-[11px]">SalonTurbo</span>
          </span>
          <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-secondary-foreground">
            6 Coins
          </span>
        </div>

        <div className="space-y-3 p-4">
          <div className="flex items-baseline justify-between">
            <p className="eyebrow text-[10px]">Tage boosten</p>
            <p className="text-[10px] text-muted-foreground">September</p>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-[9px] text-muted-foreground">
            {weekdays.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {days.map((day) => {
              const isSelected = selected.includes(day);
              const isClosed = closed.includes(day);
              return (
                <div
                  key={day}
                  className={`grid h-7 place-items-center rounded-md text-[10px] ${
                    isSelected
                      ? "bg-primary font-semibold text-primary-foreground"
                      : isClosed
                        ? "bg-muted/40 text-muted-foreground/40 line-through"
                        : "bg-muted text-muted-foreground"
                  }`}
                >
                  {day}
                </div>
              );
            })}
          </div>

          <div className="rounded-xl border bg-secondary/40 px-3 py-2.5">
            <p className="text-[11px] font-semibold">2 Tage ausgewählt</p>
            <p className="mt-0.5 text-[10px] text-muted-foreground">
              Mi 10. &amp; Do 11. September · kostet 2 Coins
            </p>
          </div>

          <div className="flex items-center justify-center gap-2 rounded-xl bg-success px-3 py-2.5 text-center text-[11px] font-semibold text-success-foreground">
            <Check className="size-4" /> Boost bestätigen
          </div>
        </div>
      </div>
    </div>
  );
}


function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="mx-auto max-w-3xl px-4 py-16">
      <h2 className="text-center font-display text-2xl font-normal sm:text-4xl">
        Häufig gestellte Fragen
      </h2>
      <p className="mt-3 text-center text-muted-foreground">
        Alles, was du vor dem Start wissen solltest.
      </p>
      <div className="mt-8 space-y-3">
        {homeFaqs.map((faq, index) => {
          const isOpen = openIndex === index;
          return (
            <div
              key={faq.question}
              className={`rounded-2xl border bg-card transition-shadow ${isOpen ? "shadow-soft" : ""}`}
            >
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : index)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
              >
                <span className="font-display text-base font-medium sm:text-lg">{faq.question}</span>
                <ChevronDown
                  className={`size-5 shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
              {isOpen && (
                <div className="px-5 pb-5">
                  <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Button asChild variant="outline">
          <Link to="/faq">Alle Fragen ansehen</Link>
        </Button>
        <Button asChild variant="ghost">
          <Link to="/preise">Preise im Detail</Link>
        </Button>
      </div>
    </section>

  );
}


function Home() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        {/* 1 – Hero */}
        <section className="mx-auto max-w-6xl px-4 pb-8 pt-12 sm:pt-16">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 eyebrow">
                <Zap className="size-3.5" /> Google-Werbung für Friseursalons
              </p>
              <h1 className="mt-5 font-display text-4xl font-light leading-[1.08] sm:text-5xl lg:text-6xl">
                Freie Termine? Mach deinen Salon genau dann bei Google sichtbar.
              </h1>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
                Wähle die Tage aus, an denen du noch Termine frei hast. SalonTurbo schaltet
                automatisch lokale Google-Suchanzeigen für deinen Salon.
              </p>
              <div className="mt-7">
                <Button asChild size="lg" className="w-full sm:w-auto">
                  <Link to="/auth">
                    SalonTurbo jetzt starten <ArrowRight className="ml-1 size-4" />
                  </Link>
                </Button>
              </div>
              <ul className="mt-6 flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:flex-wrap sm:gap-x-6">
                {[
                  "Keine Agenturkosten",
                  "Kein eigenes Google-Konto",
                  "Jederzeit pausierbar",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <Check className="size-4 shrink-0 text-success" /> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="order-first lg:order-last">
              <PhoneMockup />
            </div>
          </div>
        </section>

        {/* Für wen */}
        <section className="mx-auto max-w-6xl px-4 pb-14">
          <p className="text-center text-sm text-muted-foreground">
            Für Friseursalons, Barbershops, Stuhlmieter und Team-Salons
          </p>
        </section>

        {/* 2 – Was passiert beim Boosten */}
        <section className="border-y bg-secondary/50">
          <div className="mx-auto max-w-5xl px-4 py-16">
            <p className="eyebrow text-center text-base! sm:text-lg!">So sieht ein Boost-Tag aus</p>
            <h2 className="mt-3 text-center font-display text-2xl font-normal sm:text-4xl">
              Dein Salon wird bei Google gesehen – und direkt angerufen.
            </h2>
            <div className="mt-10">
              <BoostStorySequence />
            </div>
          </div>
        </section>

        {/* 2b – Abgrenzung zur Agentur */}
        <section className="mx-auto max-w-5xl px-4 py-16">
          <p className="eyebrow">Der Unterschied</p>
          <h2 className="mt-3 font-display text-2xl font-normal leading-snug sm:text-4xl">
            Werbung nur dann, wenn du sie brauchst.
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            Du entscheidest selbst, an welchen Tagen deine Werbung laufen soll und wann nicht. An
            ausgebuchten Tagen kannst du Salonturbo einfach aus lassen und verbrennst keinen
            einzigen Cent.&nbsp;
          </p>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <div className="rounded-3xl border bg-card p-6">
              <p className="text-sm font-medium text-muted-foreground">
                Klassische Google-Ads-Agentur
              </p>
              <ul className="mt-4 space-y-3 text-sm">
                {[
                  "Läuft dauerhaft, Monat für Monat",
                  "Ziel: allgemeine Bekanntheit",
                  "Setup, Betreuung, Retainer",
                  "Du bekommst Berichte",
                ].map((item) => (
                  <li key={item} className="flex gap-2 text-muted-foreground">
                    <X className="mt-0.5 size-4 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl border border-primary/30 bg-card p-6 shadow-soft">
              <p className="text-sm font-medium text-primary">SalonTurbo</p>
              <ul className="mt-4 space-y-3 text-sm">
                {[
                  "Läuft nur an den Tagen, die du freigibst",
                  "Ziel: genau die heute leeren Stühle füllen",
                  "Kein Vertrag, kein eigenes Google-Konto",
                  "Du bekommst Anrufe",
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <Check className="mt-0.5 size-4 shrink-0 text-success" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>


        {/* 3 – Problem */}
        <section className="mx-auto max-w-3xl px-4 py-16">
          <CalendarX2 className="size-7 text-primary" />
          <h2 className="mt-4 font-display text-2xl font-normal leading-snug sm:text-4xl">
            Ein verregneter Dienstag. Zwei Absagen. Die Fixkosten laufen weiter.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Du kennst das: Das Team steht bereit, die Farben sind gemischt, aber der ein oder andere
            Stuhl bleibt leer. Jeder freie Schnitt- oder Farbtermin kostet dich bares Geld. Bisher
            hattest du zwei Optionen: Hoffen, dass spontan jemand vorbeikommt, oder teure Rabatte
            posten, die deine Marke abwerten.
          </p>
          <p className="mt-6 font-display text-xl font-medium sm:text-2xl">
            Ab heute gibt es Option 3.
          </p>
        </section>

        {/* 4 – How it works */}
        <section className="border-y bg-secondary/50">
          <div className="mx-auto max-w-5xl px-4 py-16">
            <h2 className="font-display text-2xl font-normal sm:text-4xl">
              Vier Schritte zu mehr Terminen.
            </h2>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {[
                {
                  icon: Users,
                  title: "Salon anlegen",
                  text: "Einmalig Name, Adresse, Telefon und Öffnungszeiten hinterlegen – in unter 5 Minuten.",
                },
                {
                  icon: Zap,
                  title: "Coins aufladen",
                  text: "Ein Coin = ein Boost-Tag. Starte ab 4 Coins und lade später auf, wann du willst.",
                },
                {
                  icon: MousePointerClick,
                  title: "Tage boosten",
                  text: "Tippe im Kalender die Tage an, an denen du noch freie Termine hast.",
                },
                {
                  icon: Phone,
                  title: "Anrufe empfangen",
                  text: "Die Google-Anzeige läuft ab Mitternacht bis 2 Stunden vor Ladenschluss – Anrufe kommen rein.",
                },
              ].map((step, index) => (
                <div key={step.title} className="rounded-2xl border bg-card p-6">
                  <span className="eyebrow">
                    Schritt {index + 1}
                  </span>
                  <step.icon className="mt-3 size-6 text-primary" />
                  <h3 className="mt-3 font-display text-lg font-medium">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 5 – Feature benefits */}
        <section className="mx-auto max-w-5xl px-4 py-16">
          <div className="grid gap-6 sm:grid-cols-3">
            {[
              {
                icon: GraduationCap,
                title: "Kein Marketing-Studium nötig",
                text: "Du brauchst keinen Facebook-Manager, keine Keywords und keine Agentur. Wir haben den Aufbau für Friseursalons bereits fertig gebaut.",
              },
              {
                icon: SlidersHorizontal,
                title: "Du hast die volle Kontrolle",
                text: "Du entscheidest Tag für Tag, ob du Sichtbarkeit brauchst. An ausgebuchten Tagen bleibt SalonTurbo aus und kostet dich keinen Cent.",
              },
              {
                icon: ShieldCheck,
                title: "Dein Salon, deine Termine",
                text: "Perfekt für das gesamte Team oder einzelne Stuhlmieter, die ihren eigenen Kalender füllen wollen.",
              },
            ].map((item) => (
              <div key={item.title}>
                <item.icon className="size-6 text-primary" />
                <h3 className="mt-3 font-display text-lg font-medium">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Inspiration – visuelle Sektion */}
        <section className="border-y bg-secondary/50">
          <div className="mx-auto max-w-6xl px-4 py-16">
            <h2 className="font-display text-2xl font-normal sm:text-4xl">
              Deine Arbeit verdient volle Stühle.
            </h2>
            <p className="mt-3 max-w-xl text-muted-foreground">
              Frische Schnitte, glänzende Farben, zufriedene Kunden – SalonTurbo sorgt dafür, dass
              genau die Menschen davon erfahren, die heute noch einen Friseur-Termin suchen.
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              {[
                { src: hairBob, alt: "Frisch geschnittener glänzender Bob" },
                { src: stylistHands, alt: "Friseurin schneidet Haare mit der Schere" },
                { src: hairFade, alt: "Moderner Fade-Haarschnitt für Herren" },
              ].map((img) => (
                <div key={img.alt} className="overflow-hidden rounded-3xl shadow-soft">
                  <img
                    src={img.src}
                    alt={img.alt}
                    width={1024}
                    height={1280}
                    loading="lazy"
                    className="h-64 w-full object-cover transition-transform duration-500 hover:scale-[1.03] sm:h-80"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 6 – Pricing */}
        <section className="mx-auto max-w-4xl px-4 py-16">
          <p className="eyebrow">So funktioniert das Guthaben</p>
          <h2 className="mt-3 font-display text-2xl font-normal leading-snug sm:text-4xl">
            1 Coin = 1 Tag, an dem dein Salon beworben wird.
          </h2>
          <div className="mt-8 rounded-3xl border bg-card p-6 sm:p-8">
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
                <Zap className="size-4" /> 1 Coin
              </span>
              <ArrowRight className="size-4 text-muted-foreground" />
              <span className="inline-flex items-center gap-2 rounded-2xl border px-4 py-2 text-sm font-semibold">
                1 Boost-Tag
              </span>
            </div>

            <div className="mt-8">
              <p className="text-sm text-muted-foreground">
                Beispiel: Mo, Mi und Fr geboostet = 3 Coins
              </p>
              <div className="mt-3 grid grid-cols-7 gap-2">
                {[
                  { day: "Mo", on: true },
                  { day: "Di", on: false },
                  { day: "Mi", on: true },
                  { day: "Do", on: false },
                  { day: "Fr", on: true },
                  { day: "Sa", on: false },
                  { day: "So", on: false },
                ].map(({ day, on }) => (
                  <div
                    key={day}
                    className={`rounded-xl border px-1 py-3 text-center text-xs font-medium ${
                      on
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground"
                    }`}
                  >
                    {day}
                  </div>
                ))}
              </div>
            </div>

            <ul className="mt-8 space-y-3 text-sm sm:text-base">
              {[
                "Das Google-Werbebudget für den Tag ist im Coin enthalten.",
                "Coins verfallen nicht – du boostest tagesgenau, wann Stühle leer bleiben.",
                "Keine Grundgebühr, keine Vertragsbindung, kein eigenes Google-Konto.",
              ].map((item) => (
                <li key={item} className="flex gap-2">
                  <Check className="mt-0.5 size-4 shrink-0 text-success" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 border-t pt-5">
              <p className="text-sm text-muted-foreground">
                Preis pro Coin: {formatEuro(COIN_PRICE_CENTS)} netto (
                {formatEuro(grossFromNet(COIN_PRICE_CENTS))} inkl. 19 % MwSt.) · Start ab{" "}
                {MIN_COINS} Coins
              </p>
              <p className="mt-2 text-sm font-medium">
                Keine versteckten Agentur-Retainer von 1.000 € im Monat.
              </p>
            </div>
          </div>
        </section>


        {/* 7 – Was es ist / nicht ist */}
        <section className="mx-auto max-w-5xl px-4 py-16">
          <h2 className="font-display text-2xl font-normal sm:text-4xl">
            Was SalonTurbo ist – und was nicht.
          </h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            <div className="rounded-2xl border bg-card p-6">
              <div className="flex items-center gap-2">
                <Check className="size-5 text-success" />
                <h3 className="font-display text-lg font-medium">Das ist SalonTurbo</h3>
              </div>
              <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
                <li>Tageweise Google-Suchanzeigen für deinen Friseursalon.</li>
                <li>Eine fertige Salon-Seite mit Anruf-Button und Öffnungszeiten.</li>
                <li>Volle Kontrolle über Boost-Tage und Werbeausgaben.</li>
                <li>Ideal für Schnitt-, Farb- und Styling-Termine kurzfristig zu füllen.</li>
              </ul>
            </div>
            <div className="rounded-2xl border bg-card p-6">
              <div className="flex items-center gap-2">
                <X className="size-5 text-muted-foreground" />
                <h3 className="font-display text-lg font-medium">Das ist es nicht</h3>
              </div>
              <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
                <li>Kein Buchungssystem – Kunden rufen an oder buchen über deine bestehende Lösung.</li>
                <li>Kein Rabattportal – wir werben für deinen Salon, nicht für Preisdumping.</li>
                <li>Keine Agentur mit Monatsvertrag – du boostest nur, wenn du willst.</li>
                <li>Keine Social-Media-Betreuung – wir konzentrieren uns auf Google-Suchen.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 8 – FAQ */}
        <FaqSection />

        {/* 9 – Final CTA */}
        <section className="bg-espresso text-espresso-foreground">
          <div className="mx-auto max-w-5xl px-4 py-20 text-center">
            <p className="eyebrow text-espresso-foreground/60">Los geht's</p>
            <h2 className="mt-4 font-display text-3xl font-light sm:text-4xl">
              Bereit für einen volleren Kalender?
            </h2>
            <div className="mx-auto mt-6 h-px w-16 bg-primary" />
            <p className="mt-6 text-sm text-espresso-foreground/70">
              In wenigen Minuten eingerichtet – danach reicht ein Klick pro Tag.
            </p>
            <Button asChild size="lg" className="mt-8 w-full sm:w-auto">
              <Link to="/auth">Jetzt starten</Link>
            </Button>
          </div>
        </section>

        {/* 10 – Lead magnet, eigener Container */}
        <section className="mx-auto max-w-5xl px-4 py-16">
          <GuideCard />
        </section>


      </main>

      <SiteFooter />
    </div>
  );
}
